import { floatingAskHeidiSelectedChatAtom } from '@/atoms/floatingAskHeidiAtoms';
import { useQueryChats } from '@/hooks/query/useQueryChats';
import { cn } from '@/lib/cn';
import type { ChatListItem } from '@/types';
import { isBackendPlaceholderChatTitle } from '@/utils/chatPlaceholderTitle';
import { Button } from '@heidi/design-system/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { TypographyP2 } from '@heidi/design-system/typography';
import { useAtom } from 'jotai';
import { Check, ChevronDown, Maximize2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

// Animation timing for dropdown exit before input fade
const DROPDOWN_EXIT_DURATION = 150;

interface ThreadSelectorProps {
  disabled?: boolean;
  onOpenThread?: (chat: ChatListItem) => void;
}

/**
 * Truncate text to a maximum length with ellipsis
 */
const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 3)}...`;
};

/**
 * Get display title for a chat, using title or preview from the chat list.
 * Priority: backend title > preview > localized new-chat label
 */
const getDisplayTitle = (chat: ChatListItem, newChatLabel: string): string => {
  if (chat.title && !isBackendPlaceholderChatTitle(chat.title)) {
    return chat.title;
  }

  if (chat.preview) {
    return truncateText(chat.preview, 50);
  }

  return newChatLabel;
};

export const ThreadSelector = ({
  disabled = false,
  onOpenThread,
}: ThreadSelectorProps) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [selectedChat, setSelectedChat] = useAtom(floatingAskHeidiSelectedChatAtom);
  const { data: chatsData } = useQueryChats(1, 5);

  const newChatLabel = intl.formatMessage({
    defaultMessage: 'New Chat',
    id: 'flxJwi6Lrr',
    description: 'Label for a chat that has no custom title yet',
  });

  const viewThreadAriaLabel = intl.formatMessage({
    defaultMessage: 'View thread',
    id: '6BK9CF7ddl',
    description:
      'Accessibility label for opening the full chat thread from the thread picker',
  });

  const recentChats = useMemo(() => chatsData?.chats ?? [], [chatsData]);
  const hasRecentChats = recentChats.length > 0;
  const isNewChat = selectedChat === null;

  const handleSelectNewChat = () => {
    setSelectedChat(null);
  };

  const handleSelectChat = (chat: ChatListItem) => {
    setSelectedChat(chat);
  };

  const handleViewThread = (chat: ChatListItem, e: React.MouseEvent) => {
    e.stopPropagation();
    // Close dropdown first, then trigger open thread after exit animation
    setOpen(false);
    setTimeout(() => {
      onOpenThread?.(chat);
    }, DROPDOWN_EXIT_DURATION);
  };

  const displayText = isNewChat
    ? newChatLabel
    : getDisplayTitle(selectedChat, newChatLabel);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          className={cn(
            'max-w-[200px] gap-1 text-text-secondary hover:text-text-primary',
            'focus-visible:ring-0 focus-visible:ring-offset-0'
          )}
        >
          <TypographyP2 className="truncate">{displayText}</TypographyP2>
          <ChevronDown size={14} className="shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[280px]">
        <DropdownMenuItem
          onClick={handleSelectNewChat}
          className="flex items-center justify-between"
        >
          <span>
            <FormattedMessage
              defaultMessage="Send to new chat"
              id="e4TjnDokzs"
              description="Dropdown option to send the current message to a brand-new chat"
            />
          </span>
          {isNewChat && <Check size={16} className="text-text-primary" />}
        </DropdownMenuItem>

        {hasRecentChats && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-text-secondary">
              <FormattedMessage
                defaultMessage="Recent"
                id="Stv5CKyGo/"
                description="Section heading for recently used chats in the thread picker"
              />
            </DropdownMenuLabel>
            {recentChats.map((chat) => (
              <DropdownMenuItem
                key={chat.id}
                onClick={() => handleSelectChat(chat)}
                className="group flex items-center justify-between"
              >
                <span className="truncate">{getDisplayTitle(chat, newChatLabel)}</span>
                <div className="flex items-center gap-1">
                  {/* View icon - animated on hover */}
                  <button
                    type="button"
                    onClick={(e) => handleViewThread(chat, e)}
                    className={cn(
                      'opacity-0 group-hover:opacity-100',
                      'translate-x-2 group-hover:translate-x-0',
                      'transition-all duration-200 ease-out',
                      'rounded p-0.5 hover:bg-surface-hover'
                    )}
                    aria-label={viewThreadAriaLabel}
                  >
                    <Maximize2 size={14} className="text-text-secondary" />
                  </button>
                  {/* Checkmark for selected */}
                  {selectedChat?.id === chat.id && (
                    <Check size={16} className="shrink-0 text-text-primary" />
                  )}
                </div>
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
